import cn from 'classnames';
import {useDebouncedValue} from '@rocket-ui/react-tools';
import {FC, useCallback, useEffect, useRef, useState} from 'react';
import {useIsMaxSm} from 'features/auth/ui/AuthorizationSteps/useIsMaxSm';
import {SearchIcon} from 'shared/icons';
import {Drawer} from 'shared/ui/Drawer';
import {RegistrationCountry, useRegistrationCountries} from '../../../model';
import styles from './CountrySearchDrawer.module.css';

type Props = {
	open: boolean;
	title: string;
	selectedOksmCode: string | null;
	onClose: () => void;
	onSelect: (country: RegistrationCountry) => void;
	className?: string;
};

export const CountrySearchDrawer: FC<Props> = ({
	open,
	title,
	selectedOksmCode,
	onClose,
	onSelect,
	className,
}) => {
	const isMaxSm = useIsMaxSm();
	const [query, setQuery] = useState('');
	const debouncedQuery = useDebouncedValue(query, 300);
	const searchRef = useRef<HTMLInputElement | null>(null);

	const {countries, isLoading, isFetching} = useRegistrationCountries(debouncedQuery, {enabled: open});

	const isDebouncing = query.trim() !== debouncedQuery.trim();
	const displayCountries = isDebouncing ? [] : countries;
	const isPending = isLoading || isFetching || isDebouncing;
	const showEmptyState = !isPending && displayCountries.length === 0;

	// Фокус, когда инпут реально смонтировался (Drawer сначала возвращает null).
	const setSearchRef = useCallback((node: HTMLInputElement | null) => {
		searchRef.current = node;

		if (node) {
			node.focus();
		}
	}, []);

	useEffect(() => {
		if (!open) {
			setQuery('');
			return;
		}

		// Инпут мог остаться в DOM (анимация закрытия) — фокусим при повторном open.
		searchRef.current?.focus();
	}, [open]);

	const handleSelect = (country: RegistrationCountry) => {
		onSelect(country);
		onClose();
	};

	return (
		<Drawer
			open={open}
			onClose={onClose}
			title={title}
			titleSize="compact"
			showCloseButton
			closeButtonPosition="end"
			bodyClassName={styles.drawerBody}
			variant={isMaxSm ? 'fullscreen' : 'side'}
		>
			<div className={cn(styles.root, className)}>
				<div className={styles.searchWrap}>
					<SearchIcon className={styles.searchIcon} />
					<div className={styles.searchField}>
						<input
							ref={setSearchRef}
							type="text"
							inputMode="search"
							autoComplete="off"
							autoCorrect="off"
							spellCheck={false}
							className={cn(styles.searchInput, query && styles.searchInputWithClear)}
							placeholder="Например, Грузия"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
						/>
						<button
							type="button"
							className={cn(styles.clearButton, !query && styles.clearButtonHidden)}
							aria-label="Очистить поиск"
							aria-hidden={!query}
							tabIndex={query ? 0 : -1}
							disabled={!query}
							onClick={() => setQuery('')}
						>
							<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
								<path
									d="M6 6l12 12M18 6L6 18"
									stroke="currentColor"
									strokeWidth="1.75"
									strokeLinecap="round"
								/>
							</svg>
						</button>
					</div>
				</div>

				<ul className={styles.list} role="listbox" aria-label={title}>
					{isPending && displayCountries.length === 0 ? (
						<li className={styles.emptyState} role="none">
							Загрузка…
						</li>
					) : null}

					{displayCountries.map((country) => (
						<li key={country.oksmCode} className={styles.listItem} role="none">
							<button
								type="button"
								role="option"
								aria-selected={selectedOksmCode === country.oksmCode}
								className={cn(
									styles.option,
									selectedOksmCode === country.oksmCode && styles.optionSelected,
								)}
								onClick={() => handleSelect(country)}
							>
								{country.name}
							</button>
						</li>
					))}

					{showEmptyState ? (
						<li className={styles.emptyState} role="none">
							Ничего не найдено
						</li>
					) : null}
				</ul>
			</div>
		</Drawer>
	);
};
